import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Skeleton from "@mui/material/Skeleton";
import { useQuery } from "@apollo/client/react";
import { useParams } from "react-router";
import { CUSTOM_FIELD_TEMPLATES_QUERY } from "../../../constants/graphql/queries";
import FieldsTable from "./FieldsTable";
import EditConfigurableFieldDialog from "../../../views/settings/EditConfigurableFieldDialog";
import useFilterFields from "../../../hooks/useFilterFields";

const SkeletonFields = () => {
  return [1, 2, 3].map(item => (
    <Box key={item} mb={1}>
      <Skeleton variant="rectangular" width="100%" height={30} />
    </Box>
  ));
};

export default function ConfigurableFieldsTable({
  onAddNewField,
  enabled = true,
  displayReceivingSettings = true,
  search = "",
}) {
  const [editingField, setEditingField] = useState(null);
  const { projectId } = useParams();

  if (!enabled) return null;

  const { data: customFieldTemplatesData, loading: loadingCustomFields } = useQuery(
    CUSTOM_FIELD_TEMPLATES_QUERY,
    {
      variables: {
        projectId,
        sortBy: 'default_label',
        sortDirection: 'asc'
      }
    }
  );
  const customFieldTemplates = customFieldTemplatesData?.customFieldTemplates || [];

  const fieldsList = customFieldTemplates.map(template => [
    `custom_field_${template.id}`,
    "custom",
    template.defaultLabel,
    template,
  ]);

  const filteredFieldsList = useFilterFields(fieldsList, search);

  const handleEditField = (event, customFieldTemplate) => {
    event.stopPropagation();
    setEditingField(customFieldTemplate);
  };

  const handleCloseEditDialog = () => {
    setEditingField(null);
  };

  if (loadingCustomFields) {
    return (
      <tbody>
        <tr>
          <td colSpan={5}>
            <Box p={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Configurable Fields
              </Typography>
              <Box mt={1}>
                <SkeletonFields />
              </Box>
            </Box>
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <>
      <tbody>
        <tr>
          <td colSpan={5}>
            <Box px={2} py={0.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: "500" }}>
                Configurable Fields
                <Button
                  size="small"
                  variant="text"
                  sx={{ ml: 1.5 }}
                  startIcon={<AddIcon />}
                  onClick={onAddNewField}
                >
                  Add New Field
                </Button>
              </Typography>
            </Box>
          </td>
        </tr>
      </tbody>
      <FieldsTable
        list={filteredFieldsList.map(([name, category, label, customFieldTemplate]) => [
          name,
          category,
          <Box
            component="span"
            onClick={event => handleEditField(event, customFieldTemplate)}
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            {label}
          </Box>,
        ])}
        label=""
        enabled={enabled}
        displayReceivingSettings={displayReceivingSettings}
      />
      {editingField && (
        <EditConfigurableFieldDialog
          customFieldTemplate={editingField}
          onClose={handleCloseEditDialog}
        />
      )}
    </>
  );
}
